/**
 * Utilities for CPF formatting and validation
 */

/**
 * Formats a CPF applying the mask XXX.XXX.XXX-XX
 * @param value - CPF value without formatting
 * @returns Formatted CPF
 */
export const formatCpf = (value: string | number | null | undefined): string => {
  // Handle null, undefined, or non-string values
  if (value === null || value === undefined) {
    return '';
  }

  // Convert to string if it's a number
  const stringValue = typeof value === 'string' ? value : String(value);

  // Remove everything that is not a digit
  const numbers = stringValue.replace(/\D/g, '');

  // Apply CPF mask
  return numbers
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})/, '$1-$2')
    .slice(0, 14); // Limit to 14 characters (11 digits + 3 dots + 1 hyphen)
};

/**
 * Validates if a CPF is valid using the official algorithm
 * @param cpf - CPF with or without formatting
 * @returns true if the CPF is valid
 */
export const validateCpf = (cpf: string | number | null | undefined): boolean => {
  // Handle null, undefined, or non-string values
  if (cpf === null || cpf === undefined) {
    return false;
  }

  // Convert to string if it's a number
  const stringCpf = typeof cpf === 'string' ? cpf : String(cpf);
  const cleanCpf = stringCpf.replace(/\D/g, '');

  if (cleanCpf.length !== 11) return false;

  // Check if all digits are the same
  if (/^(\d)\1{10}$/.test(cleanCpf)) return false;

  // CPF algorithm validation
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cleanCpf.charAt(i)) * (10 - i);
  }
  let remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cleanCpf.charAt(9))) return false;

  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cleanCpf.charAt(i)) * (11 - i);
  }
  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cleanCpf.charAt(10))) return false;

  return true;
};

/**
 * Removes CPF formatting, returning only numbers
 * @param cpf - Formatted CPF
 * @returns CPF with only numbers
 */
export const cleanCpf = (cpf: string | number | null | undefined): string => {
  // Handle null, undefined, or non-string values
  if (cpf === null || cpf === undefined) {
    return '';
  }

  // Convert to string if it's a number
  const stringCpf = typeof cpf === 'string' ? cpf : String(cpf);
  return stringCpf.replace(/\D/g, '');
};
